import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useCallback, useEffect, useRef } from "react";

import { setLogoutHandler } from "@/api/logout-manager";
import { setTokenGetter } from "@/api/token-manager";

/**
 * Authentication context props
 */
export interface AuthContextProps {
	/** Kinde SDK initialization / auth error (e.g. token refresh failure) */
	authError?: string;
	email: null | string;
	isAuthenticated: boolean;
	isLoadingAuth: boolean;
	login: () => Promise<void>;
	logout: () => Promise<void>;
	name: null | string;
}

/**
 * Wraps Kinde auth for the Shadow Realm.
 *
 * Two deliberate differences from the dashboard's provider:
 *
 * 1. `login()` takes no `orgCode`. `VITE_KINDE_DOMAIN` is the Shadow Realm
 *    Kinde ENVIRONMENT's auth domain, and that environment holds a single
 *    organization, so authenticating there already resolves to it — the app
 *    never names an org.
 * 2. There is no `register()`. Self-signup is off for the whole environment,
 *    and having no registration path in the app keeps it that way even if the
 *    Kinde setting is ever flipped back. Holding an account here is the access
 *    gate, so it must be granted, never self-served.
 */
export const useAuth = (): AuthContextProps => {
	const {
		error: kindeError,
		getAccessToken: kindeGetAccessToken,
		isAuthenticated,
		isLoading: isLoadingAuth,
		login: kindeLogin,
		logout: kindeLogout,
		user: kindeUser,
	} = useKindeAuth();

	// Keep the latest Kinde helpers in refs so registering the getters once on
	// mount does not go stale when Kinde returns new function identities.
	const getAccessTokenRef = useRef(kindeGetAccessToken);
	const logoutRef = useRef(kindeLogout);
	useEffect(() => {
		getAccessTokenRef.current = kindeGetAccessToken;
		logoutRef.current = kindeLogout;
	}, [kindeGetAccessToken, kindeLogout]);

	// Hand the non-React axios client its token source and logout hook.
	useEffect(() => {
		setTokenGetter(async () => (await getAccessTokenRef.current()) ?? null);
		setLogoutHandler(() => logoutRef.current());
	}, []);

	const login = useCallback(async () => {
		await kindeLogin();
	}, [kindeLogin]);

	const logout = useCallback(async () => {
		await kindeLogout();
	}, [kindeLogout]);

	return {
		authError: kindeError ? String(kindeError) : undefined,
		email: kindeUser?.email ?? null,
		isAuthenticated,
		isLoadingAuth,
		login,
		logout,
		name:
			[kindeUser?.givenName, kindeUser?.familyName].filter(Boolean).join(" ") ||
			null,
	};
};
